import { useCrmLocale } from '../ui/locale/crmLocale';
import { useCallback, useEffect, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useToast } from '../ui/hooks';
import { QUERY_META } from '../queries/core/queryMeta';
import {
  fetchPushVapidPublicKey,
  savePushSubscription,
  syncPushSubscription,
} from '../api/pushNotifications';

interface UseGlobalPushNotificationsResult {
  isSubscribed: boolean;
  isUnavailable: boolean;
  checking: boolean;
  busy: boolean;
  enablePush: () => Promise<boolean>;
}

const pushNotificationKeys = {
  all: ['notifications', 'push'] as const,
  subscription: () => [...pushNotificationKeys.all, 'subscription'] as const,
};

const PUSH_UNAVAILABLE_STORAGE_KEY = 'push_unavailable';
const PUSH_SUBSCRIBED_STORAGE_KEY = 'push_subscribed';

function rememberPushSubscribed(subscribed: boolean) {
  try {
    if (subscribed) {
      localStorage.setItem(PUSH_SUBSCRIBED_STORAGE_KEY, 'true');
    } else {
      localStorage.removeItem(PUSH_SUBSCRIBED_STORAGE_KEY);
    }
  } catch {
    // ignore localStorage errors
  }
}

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

export function useGlobalPushNotifications(enabled = true): UseGlobalPushNotificationsResult {
  const { t: crmText } = useCrmLocale();
  const { push } = useToast();
  const [isSubscribed, setIsSubscribed] = useState(() => {
    try {
      return localStorage.getItem(PUSH_SUBSCRIBED_STORAGE_KEY) === 'true';
    } catch {
      return false;
    }
  });
  const [isUnavailable, setIsUnavailable] = useState(() => {
    try {
      return localStorage.getItem(PUSH_UNAVAILABLE_STORAGE_KEY) === 'true';
    } catch {
      return false;
    }
  });

  const syncSubscriptionQuery = useQuery({
    queryKey: pushNotificationKeys.subscription(),
    queryFn: async (): Promise<boolean> => {
      if (!('serviceWorker' in navigator) || !('PushManager' in window) || !('Notification' in window)) {
        rememberPushSubscribed(false);
        return false;
      }
      if (Notification.permission !== 'granted') {
        rememberPushSubscribed(false);
        return false;
      }
      try {
        await navigator.serviceWorker.register('/sw.js', { scope: '/' });
        const registration = await navigator.serviceWorker.ready;
        const subscription = await registration.pushManager.getSubscription();
        if (!subscription) {
          rememberPushSubscribed(false);
          return false;
        }

        rememberPushSubscribed(true);
        try {
          await syncPushSubscription(subscription);
        } catch {
          // Browser subscription is still valid; keep the UI stable and let the next sync retry.
        }
        return true;
      } catch {
        rememberPushSubscribed(false);
        return false;
      }
    },
    enabled,
    retry: 0,
    refetchOnWindowFocus: false,
    meta: QUERY_META.sensitive,
  });

  useEffect(() => {
    if (!enabled) {
      setIsSubscribed(false);
      return;
    }
    if (typeof syncSubscriptionQuery.data === 'boolean') {
      setIsSubscribed(syncSubscriptionQuery.data);
      rememberPushSubscribed(syncSubscriptionQuery.data);
      return;
    }
    if (syncSubscriptionQuery.isError) {
      setIsSubscribed(false);
      rememberPushSubscribed(false);
    }
  }, [enabled, syncSubscriptionQuery.data, syncSubscriptionQuery.isError]);

  const enablePushMutation = useMutation({
    mutationFn: async (): Promise<boolean> => {
      if (window.location.protocol !== 'https:' && window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1') {
        push?.(crmText('HTTPS required for notifications'), 'warning');
        return false;
      }

      if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
        push?.(crmText('Push notifications are not supported on this device'), 'danger');
        return false;
      }

      if (Notification.permission === 'denied') {
        push?.(crmText('Notifications are blocked in browser settings'), 'danger');
        return false;
      }

      try {
        await navigator.serviceWorker.register('/sw.js');
        const registration = await navigator.serviceWorker.ready;

        const publicKey = await fetchPushVapidPublicKey();
        const convertedKey = urlBase64ToUint8Array(publicKey);

        if (Notification.permission === 'default') {
          const perm = await Notification.requestPermission();
          if (perm !== 'granted') {
            rememberPushSubscribed(false);
            push?.(crmText('Notification permission was not granted'), 'warning');
            return false;
          }
        }

        let subscription = await registration.pushManager.getSubscription();
        if (!subscription) {
          subscription = await registration.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: convertedKey as unknown as BufferSource,
          });
        }

        await savePushSubscription(subscription);

        setIsSubscribed(true);
        rememberPushSubscribed(true);
        push?.(crmText('Push notifications enabled'), 'success');
        return true;
      } catch (err) {
        if (err instanceof DOMException && err.name === 'AbortError') {
          setIsUnavailable(true);
          try {
            localStorage.setItem(PUSH_UNAVAILABLE_STORAGE_KEY, 'true');
          } catch {
            // ignore localStorage errors
          }
          return false;
        }
        push?.(err instanceof Error ? err.message : crmText('Failed to enable push notifications'), 'danger');
        return false;
      }
    },
    meta: QUERY_META.sensitive,
  });

  const busy = enablePushMutation.isPending;
  const checking = enabled
    && syncSubscriptionQuery.isPending
    && typeof syncSubscriptionQuery.data !== 'boolean'
    && !syncSubscriptionQuery.isError;
  const refetchSubscription = syncSubscriptionQuery.refetch;

  const enablePush = useCallback(async () => {
    if (!enabled || busy) return false;
    const success = await enablePushMutation.mutateAsync();
    if (success) {
      void refetchSubscription();
    }
    return success;
  }, [busy, enablePushMutation, enabled, refetchSubscription]);

  return {
    isSubscribed,
    isUnavailable,
    checking,
    busy,
    enablePush,
  };
}

export default useGlobalPushNotifications;
